import React, { useEffect, useState, useCallback } from "react";
import { useBundleVersionCheck } from "@/hooks/useBundleVersionCheck";
import { NavLink, useNavigate } from "react-router-dom";
import {
  Search, LogOut, BarChart2, Bell, UserCircle, MessageSquare, Menu, Compass,
} from "lucide-react";
import { NAV, AUTH, NOTIF } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import SearchModal from "@/components/HotelbedsHeaderSearch";
import NavDrawer from "@/components/NavDrawer";
import CertModeChip from "@/components/CertModeChip";
import { defaultAvatarUrl } from "@/lib/avatar";

/**
 * AppShell (2026-07-14 rev — drawer swap):
 *   • Left vertical rail REMOVED.
 *   • Hamburger button (top-left of header) opens the new
 *     `<NavDrawer>` that overlays the page. Full menu structure —
 *     Home / Explore / Bookmarks / Booked Trips / Dream / Chapters
 *     (expandable) / Passport (expandable) / Lounge / Marketplace
 *     (expandable: Travel Insurance · Amazon Shopping Lists).
 *   • Right side of header keeps profile / search / messages /
 *     notifications / admin / logout — bumped up ~30% in size per
 *     user spec.
 *   • Footer and SectionRibbon unchanged.
 */

const FOOTER_LINKS = [
  { to: "/about",    label: "About Us",                testId: "footer-link-about" },
  { to: "/contact",  label: "Contact Us",              testId: "footer-link-contact" },
  { to: "/policies", label: "Policies & Disclosures",  testId: "footer-link-policies" },
];

const LOGO_URL = "/assets/logo-landscape.png";

function HeaderBadge({ count, testId }) {
  if (!count) return null;
  return (
    <span
      data-testid={testId}
      className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-tb-coral text-white text-[10px] font-ui font-bold flex items-center justify-center border border-tb-navy"
    >
      {count > 9 ? "9+" : count}
    </span>
  );
}

function PageHeader({ onOpenDrawer }) {
  const { logout, user } = useAuth();
  const nav = useNavigate();
  const [counts, setCounts] = useState({ notif: 0, msg: 0 });
  const [searching, setSearching] = useState(false);

  const fetchCounts = useCallback(async () => {
    try {
      const [n, m] = await Promise.all([
        api.get("/notifications/unread-count"),
        api.get("/messages/unread-count"),
      ]);
      setCounts({ notif: n.data.unread || 0, msg: m.data.unread || 0 });
    } catch (err) {
      console.warn("[PageHeader] badge counts failed:", err);
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    fetchCounts();
    const t = setInterval(fetchCounts, 30000);
    // 2026-07-17 — subscribe to a lightweight page-fired event so the
    // bell badge decrements the moment the user marks a notification
    // read (or clicks "Mark all read"), instead of waiting up to 30s
    // for the next poll tick. Same channel could power messages down
    // the road; for now only the notifications page dispatches it.
    const onBadgeChanged = () => { fetchCounts(); };
    window.addEventListener("travelbio:notifications-changed", onBadgeChanged);
    return () => {
      clearInterval(t);
      window.removeEventListener("travelbio:notifications-changed", onBadgeChanged);
    };
  }, [user, fetchCounts]);

  const avatar = user?.avatar_url || defaultAvatarUrl(user?.email || "");

  // ~30% larger than the previous w-3.5 h-3.5 icon set — new default
  // is w-4.5 h-4.5 with p-1.5 padding on the pill so tap targets scale
  // proportionally.
  const iconBtnCls =
    "relative rounded-full border border-tb-gold/60 p-1.5 hover:bg-tb-gold/10 tb-tap";
  const iconCls = "w-4.5 h-4.5 text-tb-gold";

  return (
    <>
      <header
        className="relative shrink-0 border-b-2 border-tb-gold/70 flex items-center justify-between px-3 gap-2"
        style={{ height: 96, backgroundColor: "#061c37" }}
      >
        <div className="flex items-center gap-2 min-w-0">
          {user && (
            <button
              type="button"
              data-testid="nav-drawer-open"
              onClick={onOpenDrawer}
              aria-label="Open navigation"
              className="w-11 h-11 rounded-full border border-tb-gold/60 bg-tb-navy/60 hover:bg-tb-gold/10 flex items-center justify-center tb-tap"
            >
              <Menu className="w-5 h-5 text-tb-gold" strokeWidth={2} />
            </button>
          )}
          <button
            type="button"
            aria-label="TravelBio home"
            onClick={() => nav(user ? "/home" : "/")}
            className="block select-none flex items-center tb-tap"
            style={{ height: 84 }}
          >
            <img
              src={LOGO_URL}
              alt="TravelBio"
              width={156}
              height={84}
              className="block select-none"
              style={{ height: 84, width: "auto", objectFit: "contain" }}
              draggable={false}
            />
          </button>
        </div>

        <div className="flex items-center gap-2">
          {/* Phase 4 (2026-07-27): cert mode indicator. Renders null
              unless HB cert isolation is active. */}
          <CertModeChip />
          {/* Advisor Atlas (Phase 1 · Slice 1a): compact "Atlas" pill
              visible ONLY when the current user is an advisor. Cert
              reviewers are explicitly excluded (backend also walls
              them off at /atlas/*). Opens the Atlas workspace in a
              new tab so the traveler surface stays parked. */}
          {user
            && Array.isArray(user.roles)
            && user.roles.includes("advisor")
            && !user.roles.includes("cert_reviewer") && (
            <button
              type="button"
              data-testid="nav-atlas-btn"
              onClick={() => window.open("/atlas/dashboard", "_blank", "noopener,noreferrer")}
              aria-label="Open Advisor Atlas"
              title="Advisor Atlas"
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-tb-gold-bright/80 bg-tb-gold/10 hover:bg-tb-gold/20 text-tb-gold-bright font-hand text-xs uppercase tracking-widest tb-tap"
            >
              <Compass className="w-4 h-4" strokeWidth={2} />
              <span className="hidden md:inline">Atlas</span>
            </button>
          )}
          {user && (
            <button
              data-testid="nav-profile-btn"
              onClick={() => nav("/profile")}
              aria-label="Your profile"
              title="Profile"
              className="rounded-full border border-tb-gold/60 p-0.5 hover:bg-tb-gold/10 tb-tap overflow-hidden bg-tb-navy"
            >
              {user.avatar_url ? (
                <img src={avatar} alt="" className="w-8 h-8 rounded-full object-cover" />
              ) : (
                <UserCircle className="w-7 h-7 text-tb-gold" strokeWidth={1.6} />
              )}
            </button>
          )}
          <button
            data-testid="nav-search-btn"
            onClick={() => {
              // Batch 1 (2026-08-07): Standard travelers land on the
              // Stay22 search page. Cert reviewers keep the HB
              // destination-autocomplete modal.
              const roles = user?.roles || [];
              if (roles.includes("cert_reviewer")) {
                setSearching(true);
              } else {
                nav("/hotels");
              }
            }}
            className={iconBtnCls}
            aria-label="Search stays"
            title="Search stays"
          >
            <Search className={iconCls} strokeWidth={1.8} />
          </button>
          {user && (
            <button
              data-testid="nav-messages-btn"
              onClick={() => nav("/messages")}
              className={iconBtnCls}
              aria-label={`Messages${counts.msg ? ` (${counts.msg} unread)` : ""}`}
              title="Messages"
            >
              <MessageSquare className={iconCls} strokeWidth={1.8} />
              <HeaderBadge count={counts.msg} testId="nav-messages-badge" />
            </button>
          )}
          {user && (
            <button
              data-testid={NOTIF.bellBtn}
              onClick={() => nav("/notifications")}
              className={iconBtnCls}
              aria-label={`Notifications${counts.notif ? ` (${counts.notif} unread)` : ""}`}
              title="Notifications"
            >
              <Bell className={iconCls} strokeWidth={1.8} />
              <HeaderBadge count={counts.notif} testId={NOTIF.unreadBadge} />
            </button>
          )}
          {user && user.role === "admin" && (
            <button
              data-testid={NAV.adminLink}
              onClick={() => nav("/admin")}
              className="rounded-full border border-tb-gold-bright/80 p-1.5 hover:bg-tb-gold/20 tb-tap bg-tb-gold/10"
              aria-label="Owner analytics"
              title="Owner Compass"
            >
              <BarChart2 className="w-4.5 h-4.5 text-tb-gold-bright" strokeWidth={2} />
            </button>
          )}
          {user && (
            <button
              data-testid={AUTH.logoutBtn}
              onClick={async () => { await logout(); nav("/auth"); }}
              className="rounded-full border border-tb-gold/60 p-1.5 hover:bg-tb-coral/20 tb-tap"
              aria-label="Logout"
              title="Logout"
            >
              <LogOut className={iconCls} strokeWidth={1.8} />
            </button>
          )}
        </div>
      </header>

      <SearchModal
        open={searching}
        onClose={() => setSearching(false)}
      />
    </>
  );
}

/** Full-width footer rendered at the bottom of every page — tagline + 3 info
 *  links. Visible on both mobile and desktop. */
function SiteFooter() {
  return (
    <footer
      data-testid="site-footer"
      className="bg-tb-navy text-tb-gold/80 border-t-2 border-tb-gold/70 px-4 py-3 shrink-0"
    >
      <p className="text-center font-hand tb-creative text-sm italic text-tb-gold/65 tracking-wide">
        your lifelong travel journal
      </p>
      <ul className="mt-1.5 flex items-center justify-center flex-wrap gap-x-4 gap-y-1">
        {FOOTER_LINKS.map((l, i) => (
          <li key={l.to} className="flex items-center gap-3">
            <NavLink
              to={l.to}
              data-testid={l.testId}
              className={({ isActive }) =>
                "font-hand text-xs hover:text-tb-gold-bright tb-tap " +
                (isActive ? "text-tb-gold-bright underline underline-offset-4" : "text-tb-gold/75")
              }
            >
              {l.label}
            </NavLink>
            {i < FOOTER_LINKS.length - 1 && (
              <span className="font-hand text-xs text-tb-gold/30 select-none">·</span>
            )}
          </li>
        ))}
      </ul>
    </footer>
  );
}

export function SectionRibbon({ children }) {
  return (
    <div className="bg-white text-tb-navy px-5 py-2 flex items-center gap-2 border-y border-tb-gold/40 shrink-0">
      <span className="w-1.5 h-1.5 rounded-full bg-tb-gold" />
      <span className="font-hand text-lg">{children}</span>
    </div>
  );
}

export default function AppShell({ children }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Runs once per mount — reloads the page if the running JS bundle
  // hash doesn't match what the server is currently serving.  Fixes
  // the "stuck on stale bundle after deploy" class of bugs.
  useBundleVersionCheck();
  return (
    <div className="h-screen w-full flex flex-col bg-tb-navy-deep overflow-hidden">
      <div className="mx-auto w-full max-w-md md:my-6 md:rounded-[2rem] md:shadow-2xl lg:my-0 lg:max-w-none lg:rounded-none lg:shadow-none flex flex-col flex-1 min-h-0 overflow-hidden tb-frame">
        <PageHeader onOpenDrawer={() => setDrawerOpen(true)} />
        <div className="flex-1 min-h-0 flex">
          <main className="flex-1 min-h-0 overflow-y-auto bg-white">
            {children}
          </main>
        </div>
        <SiteFooter />
      </div>
      <NavDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
    </div>
  );
}
